import { Modal } from '@ui-components-3/ui'
import { useOperatingHoursRanges } from 'hooks/operating-hours/useOperatingHoursRanges'
import { useOperatingHoursWeekdaySegments } from 'hooks/operating-hours/useOperatingHoursWeekdaySegments'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import WeekDayCard from './WeekDayCard'
import { endOfWeek, format, startOfWeek } from 'date-fns'
import { OPERATING_HOURS_WEEK_STARTS_ON } from 'hooks/operating-hours/utils'
import { ProcessSegmentsOptions } from 'hooks/operating-hours/types'

type ServiceHoursModalProps = {
  onClose: () => void
  selectedDate: Date
}

const ServiceHoursModal = ({ onClose, selectedDate }: ServiceHoursModalProps) => {
  const { t } = useTranslation()

  const initialDate = useMemo(
    () =>
      startOfWeek(selectedDate, {
        weekStartsOn: OPERATING_HOURS_WEEK_STARTS_ON,
      }),
    [selectedDate],
  )

  const { operatingHoursRanges } = useOperatingHoursRanges(initialDate)

  const processSegmentsOptions = useMemo<ProcessSegmentsOptions>(
    () => ({ initialDate, highlightedDate: selectedDate, excludeUnavailable: true }),
    [initialDate, selectedDate],
  )

  const weekdays = useOperatingHoursWeekdaySegments(operatingHoursRanges, processSegmentsOptions)

  const subtitle = useMemo(
    () =>
      [initialDate, endOfWeek(new Date(initialDate), { weekStartsOn: OPERATING_HOURS_WEEK_STARTS_ON })]
        .map((d) => format(d, 'MMM dd'))
        .join(' - '),
    [initialDate],
  )

  return (
    <Modal opened label={t('serviceHours.modal.title')} onClose={onClose} size="l" showHeaderCloseButton portal>
      <div className="flex flex-col gap-2 sm:gap-3">
        <p className="typography-body-xl font-medium text-neutral-800">{subtitle}</p>
        <ul className="grid list-none grid-cols-1 gap-x-4 sm:grid-cols-2 md:grid-cols-3">
          {weekdays.map((item) => (
            <WeekDayCard key={item.title} data={item} />
          ))}
        </ul>
        <div className="typography-body-l whitespace-pre-line italic text-neutral-600">
          {t('serviceHours.modal.footer')}
        </div>
      </div>
    </Modal>
  )
}

export default ServiceHoursModal
